import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { enableAdminPush, disableAdminPush, getPushState, pushSupported, badgeSupported } from "@/lib/push";
import { ShieldCheck, CreditCard, Flag, Globe, Loader2, Star, Trash2, CalendarClock, Tag, Bell } from "lucide-react";

const DURATIONS = [7, 14, 30, 60, 90];

export default function AdminSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [price, setPrice] = useState("");
  const [fPrice, setFPrice] = useState("");
  const [fDuration, setFDuration] = useState("90");
  const [fCustom, setFCustom] = useState(false);
  const [fSlots, setFSlots] = useState("0");
  const [fEnabled, setFEnabled] = useState(true);
  const [promo, setPromo] = useState({ enabled: false, price: "", start: "", end: "" });
  const [fPromo, setFPromo] = useState({ enabled: false, price: "", start: "", end: "" });
  const [availFrom, setAvailFrom] = useState("");
  const [featured, setFeatured] = useState([]);
  const [priceErr, setPriceErr] = useState("");
  const [fPriceErr, setFPriceErr] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFeatured = () => api.get("/admin/featured").then(({ data }) => setFeatured(data.featured)).catch(() => {});
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setS(data); setPrice(String(data.listing_price)); setFPrice(String(data.featured_price));
      const dur = String(data.featured_duration_days);
      if (DURATIONS.map(String).includes(dur)) setFDuration(dur); else { setFDuration(dur); setFCustom(true); }
      setFSlots(String(data.featured_max_slots)); setFEnabled(!!data.featured_enabled);
      setPromo({ enabled: !!data.promo_enabled, price: data.promo_price ? String(data.promo_price) : "", start: data.promo_start || "", end: data.promo_end || "" });
      setFPromo({ enabled: !!data.featured_promo_enabled, price: data.featured_promo_price ? String(data.featured_promo_price) : "", start: data.featured_promo_start || "", end: data.featured_promo_end || "" });
      setAvailFrom(data.featured_available_from || "");
    });
    loadFeatured();
  }, []);

  const numOk = (v) => /^\d*\.?\d+$/.test(String(v).trim()) && Number(v) >= 0;

  const saveListing = async () => {
    if (!numOk(price)) { setPriceErr("Enter a valid non-negative number."); return; }
    if (promo.enabled && !numOk(promo.price)) { setPriceErr("Enter a valid promotional price."); return; }
    setPriceErr(""); setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        listing_price: Number(price),
        promo_enabled: promo.enabled, promo_price: Number(promo.price) || 0,
        promo_start: promo.start, promo_end: promo.end });
      setS(data); toast.success("Listing pricing updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const saveFeatured = async () => {
    if (!numOk(fPrice)) { setFPriceErr("Enter a valid non-negative number."); return; }
    if (fPromo.enabled && !numOk(fPromo.price)) { setFPriceErr("Enter a valid promotional price."); return; }
    if (!/^\d+$/.test(String(fDuration)) || Number(fDuration) < 1) { toast.error("Duration must be at least 1 day"); return; }
    if (!/^\d+$/.test(String(fSlots))) { toast.error("Max slots must be a whole number (0 = unlimited)"); return; }
    setFPriceErr(""); setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        featured_price: Number(fPrice), featured_duration_days: Number(fDuration),
        featured_max_slots: Number(fSlots), featured_enabled: fEnabled,
        featured_promo_enabled: fPromo.enabled, featured_promo_price: Number(fPromo.price) || 0,
        featured_promo_start: fPromo.start, featured_promo_end: fPromo.end,
        featured_available_from: availFrom });
      setS(data); toast.success("Featured Race settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const removeFeatured = async (id) => {
    if (!window.confirm("Remove featured placement from this race?")) return;
    try { await api.post(`/admin/races/${id}/feature?featured=false`); toast.success("Featured removed"); loadFeatured(); }
    catch { toast.error("Failed"); }
  };
  const deleteFeatured = async (r) => {
    if (!window.confirm(`Delete this featured request row for "${r.name}"?\n\nThis removes it from the table. The race listing itself is not deleted.`)) return;
    try { await api.delete(`/admin/featured/${r.id}`); toast.success("Request removed"); loadFeatured(); }
    catch { toast.error("Failed"); }
  };
  const extendFeatured = async (id) => {
    const days = window.prompt("Extend featured placement by how many days?", "30");
    if (!days || !/^\d+$/.test(days)) return;
    try { await api.post(`/admin/races/${id}/feature/extend?days=${days}`); toast.success(`Extended by ${days} days`); loadFeatured(); }
    catch { toast.error("Failed"); }
  };
  const approveFeatured = async (r) => {
    const months = window.prompt(`Approve Feature Race for "${r.name}".\n\nOrganizer requested ${r.requested_months || 1} month(s). Enter the approved duration in months (1 or 3):`, String(r.requested_months || 1));
    if (!months || !/^\d+$/.test(months)) return;
    try { await api.post(`/admin/featured/requests/${r.id}/approve?months=${months}`); toast.success(`Approved for ${months} month(s) — organizer notified to pay`); loadFeatured(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const rejectFeatured = async (r) => {
    const reason = window.prompt(`Reject Featured request for "${r.name}".\n\nEnter a reason (required — the organizer will see this):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A rejection reason is required"); return; }
    const available = window.prompt("Optional: date Featured slots are available again (YYYY-MM-DD).\nLeave blank to skip.", "");
    if (available === null) return;
    try { await api.post(`/admin/featured/requests/${r.id}/reject`, { reason: reason.trim(), available_from: (available || "").trim() }); toast.success("Request rejected — organizer notified"); loadFeatured(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cur = s?.currency === "MYR" ? "RM" : (s?.currency || "RM");
  if (!s) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div>;

  return (
    <div>
      <AdminHeader title="Settings" subtitle="Platform configuration." />
      <NotificationsCard />
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 max-w-3xl">
        <div className="p-5 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-[#0a1420] text-[#22d3ee] grid place-items-center"><ShieldCheck size={18} /></span><div><div className="font-semibold text-slate-800">Signed in as</div><div className="text-sm text-slate-500">{user?.name} · {user?.email}</div></div></div>

        <div className="p-5 flex items-start gap-3" data-testid="listing-price-setting">
          <span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 grid place-items-center shrink-0"><Flag size={18} /></span>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">Calendar Listing Fee</div>
            <div className="text-sm text-slate-500 mb-2">Standard fee charged to organizers per race listing.</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden focus-within:border-[#22d3ee]">
                <span className="px-3 py-2.5 bg-slate-50 text-slate-500 text-sm font-semibold border-r border-slate-200">{cur}</span>
                <input value={price} onChange={(e) => { setPrice(e.target.value); setPriceErr(""); }} inputMode="decimal" data-testid="listing-price-input" className="w-28 px-3 py-2.5 text-sm outline-none font-mono-data" />
              </div>
              <span className="text-sm text-slate-500">per race</span>
            </div>

            <PromoBlock cur={cur} promo={promo} setPromo={setPromo} testid="listing-promo" label="listing" />

            {priceErr && <p className="text-sm text-red-600 mt-2" data-testid="listing-price-error">{priceErr}</p>}
            <button onClick={saveListing} disabled={saving} data-testid="save-price-button" className="mt-3 inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#1a2740] disabled:opacity-40">Save Listing Pricing</button>
          </div>
        </div>

        <div className="p-5 flex items-start gap-3" data-testid="featured-settings">
          <span className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 grid place-items-center shrink-0"><Star size={18} /></span>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="font-semibold text-slate-800">Featured Race Settings</div>
                <span data-testid="featured-slot-counter" className={`text-xs font-bold px-2.5 py-1 rounded-full ${Number(fSlots) > 0 && featured.filter((r) => ["active", "approved"].includes(r.status)).length >= Number(fSlots) ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {featured.filter((r) => ["active", "approved"].includes(r.status)).length} of {Number(fSlots) > 0 ? fSlots : "∞"} slots used
                </span>
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" checked={fEnabled} onChange={(e) => setFEnabled(e.target.checked)} data-testid="featured-enabled-toggle" className="w-4 h-4" />
                {fEnabled ? "Enabled" : "Disabled"}
              </label>
            </div>
            <div className="text-sm text-slate-500 mb-3">Paid promotional upgrade. All values are admin-controlled.</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Featured Race Price</label>
                <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden mt-1 focus-within:border-[#22d3ee]">
                  <span className="px-3 py-2.5 bg-slate-50 text-slate-500 text-sm font-semibold border-r border-slate-200">{cur}</span>
                  <input value={fPrice} onChange={(e) => { setFPrice(e.target.value); setFPriceErr(""); }} inputMode="decimal" data-testid="featured-price-input" className="w-full px-3 py-2.5 text-sm outline-none font-mono-data" />
                </div>
                {fPriceErr && <p className="text-xs text-red-600 mt-1">{fPriceErr}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Featured Duration</label>
                <div className="flex gap-2 mt-1">
                  <select value={fCustom ? "custom" : fDuration} onChange={(e) => { if (e.target.value === "custom") setFCustom(true); else { setFCustom(false); setFDuration(e.target.value); } }} data-testid="featured-duration-select" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#22d3ee]">
                    {DURATIONS.map((d) => <option key={d} value={String(d)}>{d} days</option>)}
                    <option value="custom">Custom…</option>
                  </select>
                  {fCustom && <input value={fDuration} onChange={(e) => setFDuration(e.target.value)} inputMode="numeric" placeholder="days" data-testid="featured-duration-custom" className="w-24 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none font-mono-data" />}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Max Active Featured Races</label>
                <input value={fSlots} onChange={(e) => setFSlots(e.target.value)} inputMode="numeric" data-testid="featured-slots-input" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#22d3ee] mt-1 font-mono-data" />
                <p className="text-xs text-slate-400 mt-1">0 = unlimited</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">"Available Again From" Date</label>
                <input type="date" value={availFrom} onChange={(e) => setAvailFrom(e.target.value)} data-testid="featured-available-from-input" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#22d3ee] mt-1" />
                <p className="text-xs text-slate-400 mt-1">Shown to organizers when all slots are full.</p>
              </div>
            </div>

            <PromoBlock cur={cur} promo={fPromo} setPromo={setFPromo} testid="featured-promo" label="featured" />

            <button onClick={saveFeatured} disabled={saving} data-testid="save-featured-button" className="mt-4 inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#1a2740] disabled:opacity-40">Save Featured Settings</button>
          </div>
        </div>

        <div className="p-5 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 grid place-items-center"><Globe size={18} /></span><div><div className="font-semibold text-slate-800">Regions</div><div className="text-sm text-slate-500">Malaysia, Singapore</div></div></div>
        <div className="p-5 flex items-center gap-3"><span className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 grid place-items-center"><CreditCard size={18} /></span><div><div className="font-semibold text-slate-800">Payment Provider</div><div className="text-sm text-slate-500">HitPay (dev fallback active until live key added)</div></div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl mt-6 max-w-5xl overflow-x-auto" data-testid="featured-status-table">
        <div className="p-5 flex items-center gap-2 border-b border-slate-100"><CalendarClock size={18} className="text-[#06b6d4]" /><h3 className="font-display font-bold uppercase text-sm text-slate-800">Featured Race Requests & Status</h3><span className="ml-auto text-xs text-slate-500">Active/committed: {featured.filter((r) => ["active", "approved"].includes(r.status)).length}{Number(fSlots) > 0 ? ` / ${fSlots} max` : " (unlimited)"}</span></div>
        {featured.length === 0 ? <p className="p-8 text-center text-slate-500 text-sm">No featured requests yet.</p> :
        <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-4">Race</th><th className="text-left p-4">Organizer</th><th className="text-left p-4">Race Date</th><th className="text-left p-4">Requested</th><th className="text-left p-4">Featured From</th><th className="text-left p-4">Until</th><th className="text-right p-4">Paid</th><th className="text-right p-4">Status</th><th className="text-right p-4">Actions</th></tr></thead>
          <tbody>{featured.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`featured-row-${r.id}`}>
              <td className="p-4 font-semibold text-slate-800">{r.name}</td>
              <td className="p-4 text-slate-600">{r.organizer_name}</td>
              <td className="p-4 text-slate-600">{(r.race_date || "").slice(0, 10) || "—"}</td>
              <td className="p-4 text-slate-600">{r.status === "approved" || r.status === "active" ? `${r.approved_months || Math.round((r.approved_days || 0) / 30)} mo` : `${r.requested_months || Math.round((r.requested_days || 0) / 30)} mo`}</td>
              <td className="p-4 text-slate-600">{(r.featured_start || "").slice(0, 10) || "—"}</td>
              <td className="p-4 text-slate-600">{(r.featured_end || "").slice(0, 10) || "—"}</td>
              <td className="p-4 text-right font-mono-data">{cur}{r.amount_paid}</td>
              <td className="p-4 text-right">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${r.status === "active" ? "bg-emerald-100 text-emerald-700" : r.status === "pending" ? "bg-blue-100 text-blue-700" : r.status === "approved" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{r.status === "approved" ? "Approved · awaiting payment" : r.status}</span>
                {r.status === "rejected" && r.reject_reason && <div className="text-[11px] text-slate-400 mt-1 max-w-[160px] ml-auto">{r.reject_reason}</div>}
              </td>
              <td className="p-4 text-right whitespace-nowrap">
                {r.status === "pending" && <>
                  <button onClick={() => approveFeatured(r)} className="text-xs font-bold text-emerald-600 hover:underline px-2" data-testid={`approve-featured-${r.id}`}>Approve</button>
                  <button onClick={() => rejectFeatured(r)} className="text-xs font-bold text-red-500 hover:underline px-2" data-testid={`reject-featured-${r.id}`}>Reject</button>
                </>}
                {(r.status === "active" || r.status === "approved") && <>
                  <button onClick={() => extendFeatured(r.id)} className="text-xs font-semibold text-[#06b6d4] hover:underline px-2" data-testid={`extend-featured-${r.id}`}>Extend</button>
                  <button onClick={() => removeFeatured(r.id)} className="text-xs font-semibold text-slate-500 hover:underline px-2" title="Remove featured placement" data-testid={`remove-featured-${r.id}`}>Remove</button>
                </>}
                <button onClick={() => deleteFeatured(r)} className="p-1.5 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500 inline-flex ml-1" title="Delete request row" data-testid={`delete-featured-${r.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>}
      </div>
    </div>
  );
}

function NotificationsCard() {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getPushState().then(setState); }, []);

  const enable = async () => {
    setBusy(true);
    try { await enableAdminPush(); setState("subscribed"); toast.success("Push notifications enabled on this device"); }
    catch (e) { toast.error(e.message || "Could not enable notifications"); setState(await getPushState()); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await disableAdminPush(); setState("granted"); toast.success("Push notifications disabled on this device"); }
    finally { setBusy(false); }
  };

  const unsupported = state === "unsupported";
  const denied = state === "denied";
  const on = state === "subscribed";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 max-w-3xl" data-testid="admin-notifications-card">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-cyan-100 text-[#06b6d4] grid place-items-center shrink-0"><Bell size={18} /></span>
        <div className="flex-1">
          <div className="font-semibold text-slate-800">Phone Push Notifications</div>
          <div className="text-sm text-slate-500 mb-3">Get an instant alert on this device when an organizer submits a race for approval or requests a Featured Race — even when the app is closed. Email alerts are always sent as a backup.</div>

          {unsupported ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="push-unsupported">
              This browser doesn't support push notifications. On Android, open YourRaceFinder in Chrome (or your installed home-screen app) and try again.
            </p>
          ) : denied ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="push-denied">
              Notifications are blocked for this site. Enable them in your browser/site settings, then reload and try again.
            </p>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              {on ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="push-status-on"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Enabled on this device</span>
                  <button onClick={disable} disabled={busy} data-testid="disable-push-btn" className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-3 py-2">{busy ? <Loader2 size={15} className="animate-spin" /> : "Turn off"}</button>
                </>
              ) : (
                <button onClick={enable} disabled={busy || state === "loading"} data-testid="enable-push-btn"
                  className="inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#1a2740] disabled:opacity-40">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} Enable Notifications
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">
            App-icon badge: {badgeSupported() ? "supported on this device — the outstanding request count will appear on your home-screen icon." : "not supported by this browser/launcher (email + in-app counts still work)."}
          </p>
        </div>
      </div>
    </div>
  );
}

function PromoBlock({ cur, promo, setPromo, testid, label }) {
  return (
    <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid={testid}>
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
        <input type="checkbox" checked={promo.enabled} onChange={(e) => setPromo({ ...promo, enabled: e.target.checked })} className="w-4 h-4" data-testid={`${testid}-toggle`} />
        <Tag size={14} className="text-[#06b6d4]" /> Run a promotion on the {label} fee
      </label>
      {promo.enabled && (
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Promo Price</label>
            <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden mt-1 focus-within:border-[#22d3ee] bg-white">
              <span className="px-2.5 py-2 bg-slate-50 text-slate-500 text-sm font-semibold border-r border-slate-200">{cur}</span>
              <input value={promo.price} onChange={(e) => setPromo({ ...promo, price: e.target.value })} inputMode="decimal" data-testid={`${testid}-price`} className="w-full px-2.5 py-2 text-sm outline-none font-mono-data" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Start Date</label>
            <input type="date" value={promo.start} onChange={(e) => setPromo({ ...promo, start: e.target.value })} data-testid={`${testid}-start`} className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-[#22d3ee] mt-1 bg-white" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">End Date</label>
            <input type="date" value={promo.end} onChange={(e) => setPromo({ ...promo, end: e.target.value })} data-testid={`${testid}-end`} className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-[#22d3ee] mt-1 bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
